/**
 * recenterSceneBoneBind — the SCENE half of unbind-adjust-rebind (the kernel
 * half is recenterBoneBind; the two are applied together by the run driver,
 * never separately). Move a skeleton bone's BIND to a new bind-world position
 * while the mesh stays exactly where it is:
 *
 *   - the bone NODE's local position moves (delta expressed in the parent's
 *     bind frame — IBM-derived, pose-independent, the same source
 *     isolate-hand trusts);
 *   - the bone's INVERSE-BIND matrix is recomputed as the exact inverse of
 *     its new bind world (rotation/scale untouched) — rest state composes
 *     boneWorld · IBM = I before and after, so no vertex moves;
 *   - CHILD bone nodes are compensated (their world binds are unchanged, so
 *     their local positions shift by the opposite delta in the bone's frame);
 *     child IBMs are untouched.
 *
 * Fail-loud: unknown bone, a bone whose parent is not a skeleton bone with an
 * IBM, or a non-invertible frame all throw — caller bugs, not outcomes.
 *
 * Depends on: three. Depended on by: app session driver (run-document).
 */
import { Bone, Matrix4, Quaternion, SkinnedMesh, Vector3 } from 'three';
import { AdapterError } from '../errors.js';

export class SceneRecenterError extends AdapterError {}

interface BindFrame {
  readonly position: Vector3;
  readonly rotation: Quaternion;
  readonly scale: Vector3;
}

function bindFrameOf(mesh: SkinnedMesh, index: number): BindFrame {
  const ibm = mesh.skeleton.boneInverses[index];
  if (ibm === undefined) {
    throw new SceneRecenterError(`skeleton has no inverse-bind matrix at index ${index}.`);
  }
  const world = new Matrix4().copy(ibm).invert();
  const position = new Vector3();
  const rotation = new Quaternion();
  const scale = new Vector3();
  world.decompose(position, rotation, scale);
  return { position, rotation, scale };
}

/**
 * Move `boneName`'s bind to `newWorldPosition` (bind-world space — the space
 * isolate-hand and the voxel grid's world embedding speak). See module header.
 */
export function recenterSceneBoneBind(
  mesh: SkinnedMesh,
  boneName: string,
  newWorldPosition: { readonly x: number; readonly y: number; readonly z: number },
): void {
  const bones = mesh.skeleton.bones;
  const index = bones.findIndex((b) => b.name === boneName);
  if (index < 0) throw new SceneRecenterError(`cannot recenter "${boneName}" — not in this skeleton.`);
  const bone = bones[index]!;

  const oldFrame = bindFrameOf(mesh, index);
  const target = new Vector3(newWorldPosition.x, newWorldPosition.y, newWorldPosition.z);
  const worldDelta = new Vector3().subVectors(target, oldFrame.position);

  // --- the bone node: delta expressed in the PARENT's bind frame ---
  const parent = bone.parent;
  if (!(parent instanceof Bone)) {
    throw new SceneRecenterError(
      `cannot recenter "${boneName}" — its parent is not a skeleton bone (roots are out of scope; ` +
        `the wrist always hangs from a forearm).`,
    );
  }
  const parentIndex = bones.indexOf(parent);
  if (parentIndex < 0) {
    throw new SceneRecenterError(`cannot recenter "${boneName}" — parent "${parent.name}" carries no IBM.`);
  }
  const parentFrame = bindFrameOf(mesh, parentIndex);
  const parentLocalDelta = worldDelta
    .clone()
    .applyQuaternion(parentFrame.rotation.clone().invert())
    .divide(parentFrame.scale);
  bone.position.add(parentLocalDelta);
  bone.updateMatrix();

  // --- child bone nodes keep their WORLD binds: opposite delta in the bone's frame ---
  const boneLocalDelta = worldDelta
    .clone()
    .applyQuaternion(oldFrame.rotation.clone().invert())
    .divide(oldFrame.scale);
  for (const child of bone.children) {
    if (!(child as Bone).isBone) continue;
    (child as Bone).position.sub(boneLocalDelta);
    child.updateMatrix();
  }

  // --- the IBM: exact inverse of the new bind world (rotation/scale untouched) ---
  const newWorld = new Matrix4().compose(target, oldFrame.rotation, oldFrame.scale);
  mesh.skeleton.boneInverses[index] = newWorld.invert();
  mesh.updateMatrixWorld(true);
}

/**
 * The same move for a skeleton SHARED by several skins (a VRoid body split
 * into body/hair/clothes: 11 of the 12 prebuilt VRoid bodies carry 14–20
 * skinned meshes on one armature; Codex P2, #178). The bone NODE (and its
 * children's compensation) moves ONCE, through the first skin that binds it;
 * every other skin that binds the bone gets its inverse bind recomputed with
 * the same world delta. Skins that do not bind the bone are untouched. The
 * caller resolves the selected skeleton's group; a skin whose bone of that
 * name is a DIFFERENT node is a caller bug and refuses. Returns how many
 * skins were rebound.
 */
export function recenterSceneBoneBindAcross(
  meshes: readonly SkinnedMesh[],
  boneName: string,
  newWorldPosition: { readonly x: number; readonly y: number; readonly z: number },
): number {
  const binding = meshes.filter((m) => m.skeleton.bones.some((b) => b.name === boneName));
  if (binding.length === 0) throw new SceneRecenterError(`cannot recenter "${boneName}" — no selected skin binds it.`);
  const first = binding[0]!;
  const node = first.skeleton.bones.find((b) => b.name === boneName)!;
  const firstFrame = bindFrameOf(first, first.skeleton.bones.indexOf(node));
  const worldDelta = new Vector3(newWorldPosition.x, newWorldPosition.y, newWorldPosition.z).sub(firstFrame.position);
  // Every other skin's new inverse bind is computed from its ORIGINAL frame
  // before anything is written — skins may share one Skeleton (one glTF skin
  // on several mesh nodes), and a write through one must not be read back as
  // another's "before".
  const pending: { mesh: SkinnedMesh; index: number; ibm: Matrix4 }[] = [];
  for (const mesh of binding.slice(1)) {
    const index = mesh.skeleton.bones.findIndex((b) => b.name === boneName);
    if (mesh.skeleton.bones[index] !== node) {
      throw new SceneRecenterError(
        `cannot recenter "${boneName}" across skins — "${mesh.name || '<unnamed>'}" binds a DIFFERENT node of that name ` +
          `(resolve the selected skeleton first).`,
      );
    }
    if (mesh.skeleton.boneInverses === first.skeleton.boneInverses) continue; // shared with the first: written there
    const frame = bindFrameOf(mesh, index);
    pending.push({
      mesh,
      index,
      ibm: new Matrix4().compose(frame.position.clone().add(worldDelta), frame.rotation, frame.scale).invert(),
    });
  }
  recenterSceneBoneBind(first, boneName, newWorldPosition);
  for (const p of pending) p.mesh.skeleton.boneInverses[p.index] = p.ibm;
  for (const mesh of binding.slice(1)) mesh.updateMatrixWorld(true);
  return binding.length;
}
